export default {
  materialTable: {
    title: 'Material properties by body',
    body: 'Body / volume',
    material: 'Material',
    allBodies: 'All bodies',
    thermalTitle: 'Thermal material properties by body',
    conductivity: 'k (W/(m·K))',
    density: 'ρ (kg/m³)',
    specificHeat: 'cp (J/(kg·K))',
  },
  feaConvergence: {
    stale:
      'No new iteration for over a minute. The solver may still be working within an iteration.',
    title: 'FEA convergence',
    attempt: 'Solve scope',
    attemptScope: 'Step {{step}} · Inc {{increment}} · Att {{attempt}}',
    attemptHelp: 'How step, increment, and attempt nest in the solve loop',
    allIncrements: 'All increments',
    iteration: 'Newton iteration',
    iterationAxis: 'Iteration',
    iterationLabel: 'Iteration {{n}}',
    // All-increments plots the final Newton of each attempt (ENG-3002), so
    // the axis is outcomes across the solve — not every opener at ~100% T fix.
    cumulativeIterationAxis: 'Final Newton per increment',
    cumulativeIterationLabel: 'Final Newton {{n}}',
    waiting: 'Waiting for Newton iterations. A direct solve may not emit any.',
    notEmitted:
      'This solve did not emit Newton iterations (direct/linear solve).',
    unavailable: 'No convergence history is available.',
    readError:
      'Could not refresh convergence history. Last received data is retained.',
    partial:
      'Partial history: {{omitted}} omitted rows, {{invalid}} invalid rows. Download the raw solver files for the complete record.',
    updated: 'Updated {{time}}',
    calibration: 'Preload calibration',
    solve: 'Final solve',
    contactCount: 'Contact elements: {{count}}',
    targetsUnavailable:
      'Convergence targets were not recorded for this run. Solver completion remains authoritative.',
    targetsDeclared:
      'Dashed lines show the primary convergence criteria. Solver completion remains authoritative because an alternative residual criterion may apply after repeated iterations.',
    met: 'Met',
    notMet: 'Not met',
    notAvailable: 'Unavailable',
    residual_force_percent: 'Force residual (%)',
    correction_displacement_percent: 'Displacement correction (%)',
    residual_flux_percent: 'Flux residual (%)',
    correction_temperature_percent: 'Temperature correction (%)',
    omittedNonpositive:
      '{{count}} chart values are 0% or below and cannot be drawn on a log scale (exact zero is common on the second Newton step).',
    // All increments keeps only the final Newton; flux/force often lands at
    // exact 0% there while earlier Newton steps still had a residual.
    allZeroFinalNewton:
      '{{measures}}: 0% at every final Newton on All increments — not drawn on the log axis. Open a single increment to see the Newton path where that residual still moves.',
    allZeroFinalNewtonTip:
      '{{measure}} is 0% at every final Newton here. Open a single increment to see its Newton path.',
    allZeroTip:
      '{{measure}} is 0% at every Newton on this scope and cannot be drawn on a log axis.',
    allZeroMark: 'all 0%',
    clippedAbove:
      '{{count}} chart values sit above this axis and are marked at the top ({{max}}%).',
    clippedMark: 'above axis',
  },
  table: {
    title: 'Design Points',
    noMatches: 'No design points match the current filters.',
  },
  // The card that owns the three sub-views and the compare pick list they
  // share. `points` is deliberately NOT "Design points": that is the Analyze
  // header's own tab, and repeating it here read as the same control twice.
  // What distinguishes this sub-view from Compare is that it holds ALL of
  // them.
  compareGutter: {
    add: 'Compare {{short}}',
    remove: 'Remove {{short}} from the comparison',
  },
  columns: {
    // Screen-reader-only names for the three columns whose visible
    // header renders nothing (ENG-1733): the row-action column, the 6px
    // left spacer that offsets the sticky index column, and the compare
    // gutter.
    actions: 'Actions',
    spacer: 'Spacer',
    compare: 'Compare',
    id: 'ID',
    geometry: 'Geometry',
    mesh: 'Mesh',
    meshTime: 'Mesh time',
    meshTimeTooltip:
      'Wall-clock time the mesher took to produce this point’s mesh, from job start to completion.',
    sim: 'Sim',
    status: 'Status',
    // Renamed from bare "Time" — the column shows the SIMULATION wall-clock
    // duration, distinct from Mesh time (the mesher's runtime shown two
    // columns left). "Sim time" parallels "Mesh time" and reads clearly
    // regardless of whether the row is queued (`queued · 12s`), running,
    // or complete.
    time: 'Sim time',
    timeTooltip:
      'Wall-clock time the solver took to run this point, from solver start to completion. Queued rows show queue-wait so far. Right-click the header to switch what this column shows.',
  },
  timeMode: {
    menuLabel: 'Show as',
    elapsed: 'Elapsed',
    elapsedHint: 'Wall-clock duration (default)',
    completedAbsolute: 'Completed at',
    completedAbsoluteHint: 'Absolute timestamp of completion',
    completedRelative: 'Completed',
    completedRelativeHint: 'Relative to now (e.g. 5m ago)',
    createdAbsolute: 'Created at',
    createdAbsoluteHint: 'Absolute timestamp of submission',
  },
  columnsMenu: {
    trigger: 'Columns',
    allOn: 'All on',
    allOnTooltip: 'Show every column',
    allOff: 'All off',
    allOffTooltip: 'Hide every result column (inputs stay visible)',
    hideNa: 'Hide N/A',
    hideNaTooltip: 'Hide columns where every design point has no value',
  },
  groups: {
    designPoint: 'Design point',
    mesh: 'Mesh',
    inputs: 'Inputs',
    run: 'Run',
    results: 'Results',
    other: 'Other',
  },
  statusBar: {
    empty: 'No design points',
    rendering: 'Showing {{from}}–{{to}} of {{total}}',
    canonicalOrder: 'Canonical order',
    sortedBy: 'Sorted by {{keys}}',
    asc: 'ascending',
    desc: 'descending',
    reset: 'Reset view',
  },
  footer: {
    meanConverged: 'Mean · converged ({{count}})',
  },
  filter: {
    clear: 'Clear filter',
  },
  toolbar: {
    exportCsv: 'Export CSV',
    searchLabel: 'Search by design point or simulation ID',
    searchPlaceholder: 'id or sim…',
    density: 'Compact rows',
  },
  header: {
    unitHint: 'Right-click to change units',
  },
  shortHeaders: {
    force_pressure_x: 'F.press X (N)',
    force_pressure_y: 'F.press Y (N)',
    force_pressure_z: 'F.press Z (N)',
    force_viscous_x: 'F.visc X (N)',
    force_viscous_y: 'F.visc Y (N)',
    force_viscous_z: 'F.visc Z (N)',
    force_normal_mag: 'F.normal |·| (N)',
    force_tangential_mag: 'F.tan |·| (N)',
    normal_tangential_ratio: 'F.normal / F.tan',
    wall_pressure_min_pa: 'Wall p min (Pa)',
    wall_pressure_max_pa: 'Wall p max (Pa)',
    inlet_flow_rate_m3s: 'Q in (m³/s)',
    outlet_flow_rate_m3s: 'Q out (m³/s)',
    inlet_velocity_avg_ms: 'U in avg (m/s)',
    outlet_velocity_avg_ms: 'U out avg (m/s)',
    mass_imbalance_frac: 'Mass imb.',
    time_step_count: 'Steps',
    last_time_step: 'Last step',
    execution_time_s: 'Solver (s)',
    solution_unphysical: 'Unphysical',
    divergence_detected: 'Diverged',
  },
  // Narrow-column abbreviations. Applied when the column shrinks below
  // COMPACT_HEADER_THRESHOLD_PX; consumed by lookupCompact() in the DP
  // table. Kept in English on the fallback so a missing entry still
  // renders something, but every locale ships its own set below.
  compactHeaders: {
    cm_pitch: 'C_M',
    normal_tangential_ratio: 'Normal / tan ratio',
    safety_factor_ultimate: 'SF ult.',
    pressure_drop_kinematic: 'Kin. Δp',
    cd: 'C_D',
    cl: 'C_L',
    force_tangential_mag: 'Tan. force',
    wall_pressure_min_pa: 'Wall p min',
    wall_pressure_max_pa: 'Wall p max',
    pressure_drop_pa: 'Δp',
    mass_imbalance_frac: 'Mass imb.',
    divergence_detected: 'Div.',
    solution_unphysical: 'Unphys.',
    converged: 'Conv.',
    iterations: 'Iter.',
    execution_time_s: 'Solver (s)',
    force_pressure_x: 'F.p X (N)',
    force_pressure_y: 'F.p Y (N)',
    force_pressure_z: 'F.p Z (N)',
    force_viscous_x: 'F.v X (N)',
    force_viscous_y: 'F.v Y (N)',
    force_viscous_z: 'F.v Z (N)',
    force_normal_mag: 'F.norm',
    force_x: 'Fx (N)',
    force_y: 'Fy (N)',
    force_z: 'Fz (N)',
    inlet_velocity_avg_ms: 'U in',
    outlet_velocity_avg_ms: 'U out',
    inlet_flow_rate_m3s: 'Q in',
    outlet_flow_rate_m3s: 'Q out',
    target_temperature_k: 'Target T',
    chip_t_max_k: 'Hot solid',
    cop_frac_lref: 'CoP frac',
    cd_band: 'C_D band',
    cl_band: 'C_L band',
    cd_drift: 'C_D drift',
    cl_drift: 'C_L drift',
    temperature_min: 'T min',
    temperature_avg: 'T avg',
    temperature_max: 'T max',
    max_displacement: 'Max disp.',
    safety_factor_yield: 'SF yield',
    material_ultimate: 'Ult.',
    col_geometry: 'Geom.',
    col_meshTime: 'Mesh t.',
  },
  // Tight-tier abbreviations — column shrunk past TIGHT_HEADER_THRESHOLD_PX
  // (~96px). Rides on top of `compactHeaders`; missing keys fall back to it.
  compactHeadersTight: {
    normal_tangential_ratio: 'N/T ratio',
    pressure_drop_kinematic: 'Δp kin',
    wall_pressure_min_pa: 'p min',
    wall_pressure_max_pa: 'p max',
    force_tangential_mag: 'F.tan',
    force_normal_mag: 'F.norm',
    mass_imbalance_frac: 'M.imb',
    divergence_detected: 'Div.',
    solution_unphysical: 'Nphys',
    target_temperature_k: 'Tgt T',
    max_displacement: 'Δmax',
    execution_time_s: 'Solver',
    chip_t_max_k: 'T sol',
    col_geometry: 'Geo.',
    col_meshTime: 'M.t.',
    col_mesh: 'M.',
  },
  // Friendly labels for `dp.inputs` keys in the detail panel — swap the raw
  // dotted API path (`inlet.velocity`, `overrides.relax_p`) for something a
  // user can parse without reading the OpenFOAM docs.
  inputLabels: {
    nu: 'Kinematic viscosity',
    rho: 'Density',
    mu: 'Dynamic viscosity',
    fluid: 'Fluid preset',
    template: 'Template',
    flowAxis: 'Flow axis',
    flowType: 'Flow type',
    turbulenceIntensity: 'Turbulence intensity',
    turbulentLengthScale: 'Turbulent length scale',
    inletVelocity: 'Inlet velocity',
    outletPressure: 'Outlet pressure',
    relaxP: 'Relaxation (pressure)',
    relaxU: 'Relaxation (velocity)',
    relaxTurbulence: 'Relaxation (turbulence)',
    gradScheme: 'Gradient scheme',
    endTime: 'End time',
    nNonOrthogonalCorrectors: 'Non-orthogonal correctors',
    writeInterval: 'Write interval',
    residualControlP: 'Residual target (pressure)',
    residualControlU: 'Residual target (velocity)',
    referenceArea: 'Reference area (A_ref)',
    referenceChord: 'Reference chord (l_ref)',
  },
  units: {
    heading: 'Units',
    rightClickHint: 'Right-click to change unit',
  },
  cell: {
    true: 'True',
    false: 'False',
  },
  action: {
    viewInMeshgen: 'View in MeshGen →',
    download: 'Download',
  },
  geometry: {
    openInMeshgen: 'Open {{label}} in MeshGen',
  },
  meshCell: {
    maxSize: 'Max element size: {{size}} mm',
    maxSizeUnset: 'Max element size: not set yet',
    cells: '{{value}} 3D cells',
    cellsLoading: 'Cell count: still loading',
    cellsUnavailable:
      'Cell count: not available (mesh still running or no count emitted)',
  },
  // DesignPointStatus labels for the status-filter popover (ENG-1744), which
  // used to render the raw wire enum with the underscores swapped for spaces.
  // Short, uniform register: these sit in a checkbox list, not a sentence.
  dpStatus: {
    pending: 'Pending',
    preprocessing: 'Preprocessing',
    meshing: 'Meshing',
    meshReady: 'Mesh ready',
    facesDone: 'Faces named',
    simulating: 'Simulating',
    extracting: 'Extracting',
    pendingReview: 'Pending review',
    completed: 'Finished runs',
    failed: 'Failed',
    cancelled: 'Cancelled',
  },
  phaseLabel: {
    failedInMesh: 'Failed in mesh',
    failedInSim: 'Failed in sim',
    cancelled: 'Cancelled',
    done: 'Done',
    simulating: 'Simulating',
    meshing: 'Meshing',
    skipped: 'Skipped',
    queued: 'Queued',
    stopped: 'Stopped',
  },
  liveDuration: {
    queued: 'queued · {{duration}}',
    queuedTooltip:
      'Waiting for the case to start. The timer switches to elapsed simulation time once the solver begins.',
  },
  attemptHistory: {
    // ENG-2935: a run's place among its point's runs, counted from the
    // oldest, so a continued run is "Run 2 of 2" rather than a repeat of
    // the attempt number it continued. The header's run picker reads it too.
    run: 'Run {{n}} of {{total}}',
    createdAt: 'Created {{time}}',
    submittedAt: 'Submitted to solver queue {{time}}',
    submittedUnknown: 'Solver submission time unavailable',
    startedAt: 'Solver started {{time}}',
    endedAt: 'Ended {{time}}',
    queueWait: 'Queue wait {{duration}}',
    iterations_one: '{{count, number}} iteration',
    iterations_other: '{{count, number}} iterations',
    // ENG-2865: a continued run counts from the start of the run it continued.
    iterationsTotal_one: '{{count, number}} iteration in total',
    iterationsTotal_other: '{{count, number}} iterations in total',
    continuedFrom: 'Continued from the run stopped at {{time}}',
    increments_one: '{{count}} increment',
    increments_other: '{{count}} increments',
    cutbacks_one: '{{count}} cutback',
    cutbacks_other: '{{count}} cutbacks',
    aiAutoBadge: 'AI · automatic',
    aiManualBadge: 'AI · manual',
    succeededFlag: 'Succeeded',
    originalRun: 'Original run',
    diagnosisHeading: 'Diagnosis',
    changesHeading: 'What changed',
    changeReasonPrefix: 'why:',
    // "SOP" was an acronym nothing on the screen spelled out, and the AI
    // retry banners now render this line on the study surface rather than
    // only inside one point's detail panel. The version still matters — it
    // says which rulebook the advisor was following — so it is the word that
    // changed, not the fact.
    sopVersion: 'Retry rules v{{v}}',
    copy: 'Copy',
    copied: 'Copied',
    copyTooltip:
      'Copy the full attempt log as plain text — ready to paste into a chat or message.',
    logHeader: 'Attempt log · case {{id}}',
  },
  decisions: {
    heading_one: 'Retry decision',
    heading_other: 'Retry decisions',
    badge_one: '{{count}} verdict',
    badge_other: '{{count}} verdicts',
    reasonPrefix: 'Why:',
    verdict: {
      retry: 'Retry',
      escalate: 'Escalated to a human',
      decline: 'Declined',
    },
    lifecycle: {
      proposed: 'proposed',
      dispatched: 'dispatched',
      dismissed: 'dismissed',
      approved: 'approved',
    },
    confidence: {
      low: 'low',
      medium: 'medium',
      high: 'high',
    },
    reason: {
      semanticFailure: 'the failure is not one a parameter change can fix',
      advisorDeclined: 'the advisor declined to propose a change',
      lowConfidence: 'the proposal was below the confidence bar',
      advisorInvalid: 'the advisor returned an unusable proposal',
      advisorTimeout: 'the advisor did not answer in time',
      validationFailed: 'the proposed parameters failed validation',
      previouslyTried: 'the same change had already been tried',
      capExhausted: 'the automatic retry limit was reached',
      gciIneligible: 'this design point is not eligible for auto-retry',
      advisorNotEntitled:
        'the workspace or user has no access to the retry advisor',
      engineerRerun: 're-run by the engineer with unchanged settings',
    },
  },
  residualChart: {
    title: 'Residual Convergence',
    loading: 'Loading convergence data…',
    notAvailable: 'Residual data not available for this run.',
    awaitingFirstFlush: 'Waiting for the first solver iterations…',
    notApplicableSolver:
      'This solver does not publish a residual history. Convergence is reported per increment in the solver log rather than as per-field residuals.',
    noIterationsEmitted:
      'This run failed before the solver emitted any iterations.',
    divergedNan: 'diverged (NaN/Inf)',
    diverging: 'diverging',
    iterationAxis: 'Iteration',
    iterationLabel: 'Iteration {{n}}',
    convergeExitThreshold: 'Converge exit threshold',
    convergeExitThresholdTooltip:
      'Dashed line: the residual this equation had to reach for the solver to exit as converged. Shown when the run stopped before those targets were met.',
    residualTarget: 'Residual target',
    residualTargetTooltip:
      'Dashed line: the residual target this equation was given. The run diverged before those targets were met — compare each curve to its own dashed line.',
    convergeExitShort: 'Converge exit',
    residualTargetShort: 'Residual target',
  },
  masterTable: {
    rowOf: 'row {{current}} of {{total}}',
    rowCount: '{{count}} rows',
    headerHintSort: '{{label}} · click to sort',
    headerHintUnits: '{{label}} · click to sort · right-click to change units',
    simCellTitle: 'simulation {{sim}} · attempt {{attempt}} of {{total}}',
  },
  withDetail: {
    reopenTooltip: 'Re-open detail (Enter)',
  },
  strip: {
    regionLabel: 'Design points',
    count_one: '{{count}} point',
    count_other: '{{count}} points',
    expandLabel: 'Show all design points',
    rowLabel: '#{{n}} {{short}} · {{status}}',
    compare: 'Compare picked points…',
  },
  jumpStrip: {
    regionLabel: 'Jump to section',
    jumpTo: 'Jump to:',
    newBadge: 'New',
    pendingTooltip: 'Not available yet — waiting on the run to finish.',
    visualization: 'Visualization',
    residual: 'Residual chart',
    feaConvergence: 'Convergence chart',
    lineProbe: 'Line probe',
    feaSetup: 'Setup',
    inputs: 'Inputs',
    results: 'Results',
    massInertia: 'Mass & inertia',
    failureDetail: 'Failure detail',
    attemptHistory: 'Attempt history',
    metadata: 'Metadata',
    verification: 'Verification',
    sandbox: 'Sandbox',
    sandboxHandCalc: 'Hand-Calc',
    sandboxConsistency: 'Consistency Check',
    sandboxPlotting: 'Plotting Set',
  },
  emptyDetail: {
    title: 'Select a design point',
    description:
      'Click any row to see inputs, results, and residual convergence here.',
    firstFailure: 'First failure',
    bestSoFar: 'Best so far',
    firstRow: 'First row',
    kbdStep: 'step',
    kbdClose: 'close',
    kbdReopen: 're-open last',
  },
  section: {
    resultFieldOnGeometry: 'Flow field on the body surface',
    resultTemperatureOnGeometry: 'Temperature on the body surface',
    resultFieldOnModel: '{{field}} on the model',
    resultFeaHeatmap: 'Stress / displacement heatmap',
    feaSetup: 'Setup — fixtures and loads',
    feaSetupThermal: 'Setup — thermal boundary conditions',
    residualConvergence: 'Residual convergence',
    lineProbe: 'Field along a line',
    inputs: 'Inputs',
    results: 'Results',
    massInertia: 'Mass & inertia',
    failureDetail: 'Failure detail',
    attemptHistory: 'Attempt history',
    metadata: 'Metadata',
    files: 'Files',
    sandbox: 'Sandbox',
  },
  meshInfo: {
    elementType: 'element type',
    strategy: 'strategy',
    maxSize: 'max size',
    minSize: 'min size',
    elementOrder: 'element order',
    cells: 'cells',
    boundaryLayer: 'boundary layer',
    skewnessMax: 'skewness (max)',
    aspectRatioMax: 'aspect ratio (max)',
    bodySurfaceArea: 'body surface area',
    inletArea: 'inlet area',
    outletArea: 'outlet area',
    enclosureArea: 'enclosure surface area',
    projectedFrontalArea: 'frontal area ({{axis}})',
    bodyBbox: 'body bounding box (X × Y × Z)',
    enclosureBbox: 'enclosure bounding box (X × Y × Z)',
    bodyVolume: 'body volume',
    enclosureVolume: 'enclosure volume',
  },
  meshInfoTooltip: {
    bodySurfaceArea:
      'Total wetted area of the body walls. Excludes the enclosure sides (external flow) and the inlet / outlet planes.',
    inletArea: 'Total area of the inlet faces.',
    outletArea: 'Total area of the outlet faces.',
    enclosureArea:
      'Total area of the enclosure sides (external flow) — every non-body, non-inlet, non-outlet face combined.',
    projectedFrontalArea:
      'The body’s silhouette as seen by the incoming flow. Use this area to calculate drag and lift coefficients.',
    bodyBbox:
      'The body’s width along X, Y and Z, measured from the imported geometry.',
    enclosureBbox:
      'The enclosure’s width along X, Y and Z, estimated from its mesh faces.',
    bodyVolume:
      'The volume of the imported body. For older geometry without a saved volume, estimated from the mesh surface.',
    enclosureVolume:
      'The volume inside the enclosure, estimated from its mesh faces.',
  },
  meta: {
    designPointLabel: 'design point label',
    simulationId: 'Simulation ID',
    meshId: 'Mesh ID',
    geometryId: 'Geometry',
    meshTags: 'mesh tags',
    status: 'status',
    created: 'created',
    started: 'started',
    completed: 'completed',
  },
  massProperties: {
    component: 'Component',
    material: 'Material',
    volume: 'Volume (m³)',
    mass: 'Mass (kg)',
    comX: 'Center of mass X (m)',
    comY: 'Center of mass Y (m)',
    comZ: 'Center of mass Z (m)',
    ixx: 'Mass moment I_xx (kg·m²)',
    iyy: 'Mass moment I_yy (kg·m²)',
    izz: 'Mass moment I_zz (kg·m²)',
    ixy: 'Product of inertia I_xy (kg·m²)',
    ixz: 'Product of inertia I_xz (kg·m²)',
    iyz: 'Product of inertia I_yz (kg·m²)',
    kx: 'Radius of gyration k_x (m)',
    ky: 'Radius of gyration k_y (m)',
    kz: 'Radius of gyration k_z (m)',
  },
  // ENG-2761. A run that stopped early now shows the numbers it managed to
  // save. They render in the same table as a finished run's, so this names
  // what they are. Plain wording on purpose: "last written time directory"
  // is our vocabulary, not the reader's.
  partial: {
    title: 'Partial results',
    body: 'This run stopped before it finished. The numbers below come from the last step it saved (step {{step}}), so they are not a finished answer and have not been checked.',
    bodyTime:
      'This run stopped before it finished. The numbers below come from the last point in time it saved ({{step}} s of simulated time), so they are not a finished answer and have not been checked.',
    bodyIncrement:
      'This run stopped before it finished. The numbers below come from the last part of the load that solved (increment {{step}}), so they are not a finished answer and have not been checked.',
    bodyNothingRecovered:
      'This run stopped before it finished, and no results could be recovered from it. The failure details below are all there is.',
    bodyNoStep:
      'This run stopped before it finished. The numbers below come from the last step it saved, so they are not a finished answer and have not been checked.',
    sourceLatestTime: 'The flow and temperature fields are from that step.',
    sourceLastIncrement:
      'The stresses and displacements are from the last part of the load that solved, not the full load you asked for.',
    sourceUnknown: '',
  },
  detail: {
    prev: 'Previous design point (↑)',
    next: 'Next design point (↓)',
    viewAttempt: 'View run',
    viewCurrentAttempt: 'View latest attempt',
    attemptLoading: 'Loading this attempt’s saved results…',
    attemptLoadFailed: 'Couldn’t load this attempt’s saved results.',
    historicalHandCalc:
      'Hand Calc is saved for the latest attempt only. Select the latest attempt to review it.',
    indexOf: '#{{current}} of {{total}}',
    // Running solve's progress bar beside the elapsed time (ENG-2920).
    runProgressLabel: 'Run progress',
    runProgressIterations: 'iter {{done}} / {{target}}',
    runProgressSeconds: 't = {{done}} / {{target}} s',
    runProgressRunning: 'Running',
    openInMeshgen: 'Open in MeshGen',
    inputCase: 'Input Case',
    inputCaseTooltip:
      'Download the files sent to the solver. Find results and logs in Attempt history.',
    output: 'Output',
    outputTooltip: 'Download output (results & solver logs)',
    close: 'Close detail (Esc)',
    // The limit a stopped run reached, on the header's status chip.
    stopLimit: {
      iterationLimit: 'iteration limit',
      timeLimit: 'time limit',
    },
    download: 'Download',
    tagSweep: 'Sweep',
    tagManual: 'Manual',
    // Terminal-outcome word rendered before the duration in the detail
    // header ("Diverged in 4m 15s", "Converged 5m 3s"). See run-outcome.ts.
    outcomeConverged: 'Converged',
    outcomeVerified: 'Verified',
    outcomeVerifiedByReviewer: 'Verified by a reviewer',
    outcomeStopped: 'Stopped',
    outcomeGuide: 'Run status meanings',
    outcomeExplanations: {
      verified:
        'Completed with a full automated pass, or an editor acknowledged remaining checks (including via Hand Calc relation approval). The solver result is unchanged.',
      reviewed:
        'An editor accepted the named unresolved checks. The solver result is unchanged.',
      converged:
        'Completed without diverging. Full verification may still be incomplete — check the recorded convergence, or use Review verification to acknowledge remaining limitations.',
      stopped:
        'Reached a time limit before the solve finished. A saved checkpoint may allow continuation.',
      diverged: 'Numerical failure prevented a reliable solution.',
      failed:
        'Ended without a usable solution for another reason. See Failure detail.',
    },
    outcomeCompleted: 'Completed',
    outcomeDiverged: 'Diverged',
    outcomeFailed: 'Failed',
    outcomeTooltip:
      'Verified means the run completed and passed every applicable verification check, or an editor acknowledged remaining limitations. Converged is the provisional label for a completed, non-diverged run without full verification — including measured non-convergence and iteration-limit stops. Diverged means numerical failure. Failed means the run ended without a solution for another reason; check Failure detail for the cause. Stopped means the run reached a time limit before a final solution. Only runs with a saved checkpoint can be continued.',
    fullSolverSetup:
      'Full submitted solver setup (initial and load conditions)',
    failedVerification:
      'This run failed before a verified solution. Any recorded residuals or last-step values are partial diagnostics, not a passing verification verdict.',
    best: 'best',
    logAxisTooltip:
      'The vertical scale shows large and small residuals on one chart. Each grid step changes by a factor of ten.',
    yAxisLog: 'y-axis: log',
    noSimulationYet:
      'No simulation yet — convergence data appears once a run starts.',
    parameterCount: '{{count}} parameters',
    resultsHint: 'right-click a row for units',
    massInertiaHint: 'about the centre of mass, global axes',
    componentMassProperties: 'Component properties (body COM, global axes)',
    componentMassUnavailable:
      'Component mass properties are unavailable for this legacy geometry. Re-diagnose and rerun it to populate per-body inertia.',
    resultsHintWithFrontier: 'vs frontier best · right-click a row for units',
    noValues: 'No values to display.',
    attemptCount: '{{count}} attempts',
    noAttempts: 'No attempts yet.',
    filesEmpty:
      'No simulation attempts yet — files will appear once a simulation runs.',
    attemptLabel: 'attempt {{n}}',
    retryWithAi: 'Retry with AI',
    retryWithAiTooltip:
      "Khorium's agent reviews this case's setup and failure details, adjusts the solver settings, and runs it again.",
    retryQueuedToast: 'Retry sent — the agent queued attempt {{n}}.',
    retryFailedToast: 'Retry not started: {{message}}',
    retryAnyway: 'Retry anyway',
    retryAnywayTooltip:
      "Run it again with the same settings, without the advisor's changes",
    retryAnywayQueuedToast:
      'Retry sent — attempt {{n}} queued with the same settings.',
    autoRetryFailedToast: "Couldn't update automatic retry: {{message}}",
    // ENG-2865: a stopped OpenFOAM run carries on from where it stopped.
    continueRun: 'Continue run',
    continueRunTooltip:
      'Carry on from where this run stopped, with a higher iteration or time limit',
    continueQueuedToast: 'Run continued — it picks up from where it stopped.',
    continueFailedToast: 'Run not continued: {{message}}',
    continueOutcomeUnknownToast:
      "Couldn't confirm the run continued. Refreshing the points to show where it stands.",
  },
  continueDialog: {
    title: 'Continue this run',
    iterationLimit:
      'The run reached its iteration limit at iteration {{checkpoint}} before it settled. Continuing picks up from there instead of starting over.',
    timeLimit:
      'The run ran out of time. Continuing picks up from where it stopped instead of starting over.',
    postprocessTimeLimit:
      'The run ran out of time, and then its results could not be read. Continuing picks up from where it stopped and reads them again, instead of starting over.',
    postprocessFailed:
      'The run finished, but its results could not be read. Continuing picks up from its last saved point and reads them again, instead of starting over.',
    reachedIterationLimit:
      'The run reached its iteration limit at iteration {{checkpoint}}. Continue it with a higher iteration limit to pick up from there instead of starting over.',
    convergedAtIterationLimit:
      'The run reached its iteration limit at iteration {{checkpoint}} without diverging, so it counts as converged. Continue it with a higher iteration limit to keep iterating from there instead of starting over.',
    reachedEndTime:
      'The run reached its iteration limit at iteration {{checkpoint}}, but its results could not be read. Set a higher iteration limit to continue from there instead of starting over.',
    interrupted:
      'The run was interrupted before it finished — the machine it was on went away, not a problem with your setup. Continuing picks up from its last saved point instead of starting over.',
    iterations: 'New iteration limit',
    iterationsHint: 'Must be more than {{checkpoint}}, where the run stopped.',
    cancel: 'Cancel',
  },
} as const;
